import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { UseFieldArrayRemove, UseFormRegister } from 'react-hook-form';
import JobContentItem from './JobContentItem';
import type { ProvidedJobPostsFormSchema } from '../schemas/providedJobPostsFormSchema';

type Props = {
  fields: Array<{
    id: string;
    key: string;
    content?: string;
  }>;
  jobContentJson: Array<{
    key: string;
    content?: string;
  }>;
  register: UseFormRegister<ProvidedJobPostsFormSchema>;
  remove: UseFieldArrayRemove;
  move: (from: number, to: number) => void;
};

export default function JobContentsDnd({ fields, jobContentJson, register, remove, move }: Props) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const from = fields.findIndex((field) => field.id === active.id);
        const to = fields.findIndex((field) => field.id === over.id);
        move(from, to);
      }
    },
    [fields, move]
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={fields} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {fields.map((field, index) => (
            <JobContentItem
              key={field.id}
              id={field.id}
              field={jobContentJson[index]}
              index={index}
              remove={remove}
              register={register}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
